// Clinical regimen products, quiz, framework and recommendation logic.
// The "framework" model replaces the sales-y five-brand recommendation with
// a generic, dermatologist-friendly protocol. JAKS products are surfaced as
// optional companions per step; Relumia is featured as the flagship oral
// support.

export const PRODUCTS = {
  facewash: {
    id: "facewash",
    name: "Faciox Vitamin C Face Wash",
    role: "Cleanser",
    step: "Cleanse",
    desc: "Gentle daily cleanser with vitamin C that lifts impurities and oil while brightening dull, tired skin.",
  },
  serum: {
    id: "serum",
    name: "Faciox C 20% Vitamin C Serum",
    role: "Active Serum",
    step: "Treat",
    desc: "High-potency 20% vitamin C serum targeting pigmentation, dark spots, dullness and uneven tone.",
  },
  moisturizer: {
    id: "moisturizer",
    name: "Emolux OC Moisturizer",
    role: "Moisturizer",
    step: "Hydrate",
    desc: "Lightweight, oil-control moisturiser that hydrates and restores the barrier without clogging pores.",
  },
  sunscreen: {
    id: "sunscreen",
    name: "Faciox UV Sunscreen",
    role: "Sunscreen",
    step: "Protect",
    desc: "Broad-spectrum SPF that prevents tanning, dark spots and photo-ageing.",
  },
  relumia: {
    id: "relumia",
    name: "Relumia",
    role: "Oral Glutathione",
    step: "From Within",
    desc: "Oral glutathione (OPITAC™) with Vitamin C, D3, Zinc & Curcumin — supports brighter, even-toned, youthful skin from within.",
  },
};

export const QUESTIONS = [
  {
    id: "type",
    q: "How would you describe your skin?",
    help: "This helps us match textures your barrier will tolerate.",
    options: [
      { value: "oily", label: "Oily — shine & visible pores" },
      { value: "combination", label: "Combination — oily T-zone, dry cheeks" },
      { value: "dry", label: "Dry — tight, flaky or rough" },
      { value: "normal", label: "Normal — generally balanced" },
      { value: "sensitive", label: "Sensitive — reacts & reddens easily" },
    ],
  },
  {
    id: "concern",
    q: "What is your primary concern right now?",
    help: "We prioritise your framework around this.",
    options: [
      { value: "acne", label: "Acne & breakouts" },
      { value: "dullness", label: "Dullness & uneven tone" },
      { value: "pigmentation", label: "Dark spots & pigmentation" },
      { value: "ageing", label: "Fine lines & early ageing" },
      { value: "redness", label: "Sensitivity & redness" },
    ],
  },
  {
    id: "routine",
    q: "What does your current routine look like?",
    help: "Your skin history guides how gradually we introduce actives.",
    options: [
      { value: "none", label: "Nothing regular" },
      { value: "basic", label: "Cleanser only" },
      { value: "moderate", label: "Cleanse + moisturise" },
      { value: "advanced", label: "Actives / serums already" },
    ],
  },
  {
    id: "sun",
    q: "How much daily sun exposure do you get?",
    help: "UV is the biggest driver of pigmentation & ageing.",
    options: [
      { value: "high", label: "High — outdoors often" },
      { value: "moderate", label: "Moderate — some commuting" },
      { value: "low", label: "Minimal — mostly indoors" },
    ],
  },
  {
    id: "history",
    q: "Have you used prescription actives or had reactions?",
    help: "So we can flag anything to review with a clinician.",
    options: [
      { value: "never", label: "Never used actives" },
      { value: "otc", label: "Used OTC actives comfortably" },
      { value: "rx", label: "Used prescription actives (retinoids, etc.)" },
      { value: "reacted", label: "Had irritation / reactions before" },
    ],
  },
];

// Deterministic framework — generic clinical steps + optional JAKS companions.
// Relumia is the featured hero (oral, brand-adjacent) rather than one of many.
export function recommendProtocol(a) {
  const concern = a.concern;
  const sensitive = a.type === "sensitive" || concern === "redness" || a.history === "reacted";
  const dryish = a.type === "dry";
  const wantsBrightening = ["dullness", "pigmentation", "ageing"].includes(concern);

  const notes = [];
  if (a.sun === "high") notes.push("Your sun exposure is high — daily broad-spectrum SPF (reapplied every 3–4 hours) is the single most important step in any framework.");
  if (a.history === "rx") notes.push("Since you've used prescription actives, ask your dermatologist how to layer them alongside any new products.");
  if (a.routine === "none" || a.routine === "basic") notes.push("Introduce one new product at a time over 1–2 weeks to let your skin adjust.");
  if (sensitive) notes.push("With reactive skin, patch-test any new active on the inner forearm for 48 hours before facial use.");
  if (concern === "acne") notes.push("Cleansing consistency and oil-control hydration form the base of acne care. Discuss targeted actives (retinoids, azelaic acid, benzoyl peroxide) with a dermatologist.");

  // Framework steps — generic guidance + optional JAKS pill
  const steps = [
    {
      key: "cleanse",
      icon: "droplet",
      title: "Cleanse",
      time: "AM & PM",
      guidance: "A gentle, sulphate-conscious face wash twice daily. Look for Vitamin C or niacinamide if you want a brightening boost, or salicylic acid if you're prone to breakouts.",
      jaks: {
        id: "facewash",
        name: PRODUCTS.facewash.name,
        note: "Vitamin C-enriched daily cleanser from the JAKS range.",
      },
    },
    ...(wantsBrightening ? [{
      key: "correct",
      icon: "sparkle",
      title: "Correct",
      time: sensitive ? "Alternate nights (introduce slowly)" : "AM (or PM)",
      guidance: sensitive
        ? "A stabilised, buffered topical Vitamin C (5–10%) or azelaic acid. Introduce slowly and monitor for stinging."
        : "A stabilised topical Vitamin C serum (10–20%) to target dullness, spots and uneven tone. Pair with sunscreen the following morning.",
      jaks: {
        id: "serum",
        name: PRODUCTS.serum.name,
        note: sensitive
          ? "Available if your dermatologist confirms tolerance — introduce at low frequency."
          : "20% stabilised Vitamin C option from the JAKS range.",
      },
    }] : []),
    {
      key: "hydrate",
      icon: "cloud",
      title: "Hydrate",
      time: "AM & PM",
      guidance: dryish
        ? "A rich, ceramide-based moisturiser to restore the barrier. Look for hyaluronic acid, glycerin and ceramides."
        : "A lightweight, non-comedogenic moisturiser. Look for hyaluronic acid, niacinamide and oil-control esters if your T-zone shines.",
      jaks: {
        id: "moisturizer",
        name: PRODUCTS.moisturizer.name,
        note: "Non-comedogenic oil-control formula from the JAKS range.",
      },
    },
    {
      key: "protect",
      icon: "sun",
      title: "Protect",
      time: "AM (reapply every 3–4 hours outdoors)",
      guidance: "Broad-spectrum SPF 50, PA+++ or higher, applied as the last morning step. This is non-negotiable for any pigmentation or anti-ageing goal.",
      jaks: {
        id: "sunscreen",
        name: PRODUCTS.sunscreen.name,
        note: "Broad-spectrum SPF option from the JAKS range.",
      },
    },
  ];

  // Regimen schedule (AM / PM) built from framework guidance
  const am = ["Gentle cleanser", ...(wantsBrightening && !sensitive ? ["Stabilised Vitamin C serum"] : []), "Moisturiser", "Broad-spectrum SPF 50+"];
  const pm = ["Gentle cleanser", ...(wantsBrightening ? [sensitive ? "Vitamin C (alternate nights)" : "Vitamin C or retinoid (as advised)"] : []), "Moisturiser"];
  const oral = wantsBrightening
    ? [`${PRODUCTS.relumia.name} — 1 tablet daily after a meal, as advised by your clinician`]
    : [];

  return {
    steps,
    hero: {
      show: wantsBrightening,
      product: PRODUCTS.relumia,
      pitch: wantsBrightening
        ? "For visible brightness that skincare alone can't reach, JAKS Pharma's flagship glutathione — Relumia — supports skin from within with OPITAC™ Japanese glutathione, Vitamin C, D3, Zinc and Curcumin."
        : "",
    },
    regimen: { am, pm, oral },
    notes,
  };
}
